import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';

/** The parts of a page theme that the block pictures of the picker draw with. */
export interface PickerThemeLook {
  /** The page ground: the theme art over the theme background. */
  ground: string;
  ink: string;
  accent: string;
  card: string;
  line: string;
  button: string;
  buttonInk: string;
  radius: string;
  buttonRadius: string;
  avatarRadius: string;
}

/**
 * Each page theme as the picker pictures draw it (design smartlink-editor b1: the block drawn in
 * CSS on the page's own theme, not a screenshot).
 *
 * Copied from the theme files of sml-preview (`sml-preview/app/src/assets/styles/themes/<theme>.scss`):
 * `background-art` + `background-color`, `body-color`, `accent-color`, `card-background`,
 * `line-color`, `button-fill` (else `button-color`), `button-ink` and the three radii (0 is written
 * `0px`: the CSS uses the radii in `min()`, which refuses a bare 0). When a theme
 * file changes, change its row here too.
 */
export const pickerThemeLooks: Record<SerializedSmartLinkVersionTheme, PickerThemeLook> = {
  [SerializedSmartLinkVersionTheme.chill]: {
    ground:
      'radial-gradient(42% 26% at 82% 8%, rgba(255, 196, 168, 0.75), transparent 70%), radial-gradient(46% 28% at 6% 58%, rgba(255, 186, 214, 0.6), transparent 72%), radial-gradient(50% 30% at 60% 100%, rgba(255, 214, 180, 0.55), transparent 70%), linear-gradient(152deg, #efdcfb 0%, #e9d9fc 38%, #f7e4e9 72%, #ebdcfd 100%)',
    ink: '#120c1c',
    accent: '#6417fb',
    card: 'rgba(255, 255, 255, 0.55)',
    line: 'rgba(18, 12, 28, 0.34)',
    button: '#120c1c',
    buttonInk: '#ffffff',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.deep]: {
    ground:
      'repeating-radial-gradient(circle at 22% 8%, rgba(150, 130, 255, 0.16) 0 1px, transparent 1px 62px), #1f212b',
    ink: '#ffffff',
    accent: '#8b7bff',
    card: 'rgba(255, 255, 255, 0.05)',
    line: 'rgba(255, 255, 255, 0.3)',
    button: '#ffffff',
    buttonInk: '#1f212b',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.dark]: {
    ground: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent 40%), #1f1f1f',
    ink: '#ffffff',
    accent: '#c82b2b',
    card: '#282828',
    line: 'rgba(255, 255, 255, 0.14)',
    button: '#a42222',
    buttonInk: '#ffffff',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.loli]: {
    ground:
      'radial-gradient(150% 34% at 18% 86%, rgba(150, 110, 255, 0.38), transparent 62%), radial-gradient(130% 30% at 84% 66%, rgba(120, 74, 240, 0.3), transparent 64%), radial-gradient(170% 40% at 46% 104%, rgba(96, 54, 214, 0.44), transparent 66%), linear-gradient(180deg, #221052 0%, #1c0d41 55%, #170a36 100%)',
    ink: 'rgba(255, 255, 255, 0.92)',
    accent: '#b79cff',
    card: 'rgba(255, 255, 255, 0.09)',
    line: 'rgba(255, 255, 255, 0.34)',
    button: '#ffffff',
    buttonInk: '#2c0a6b',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.paper]: {
    ground: 'repeating-linear-gradient(180deg, rgba(21, 20, 14, 0.035) 0 1px, transparent 1px 26px), #f2efe7',
    ink: '#15140e',
    accent: '#b23a1b',
    card: 'transparent',
    line: 'rgba(21, 20, 14, 0.22)',
    button: '#15140e',
    buttonInk: '#f2efe7',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.neon]: {
    ground:
      'repeating-linear-gradient(0deg, rgba(182, 255, 46, 0.055) 0 1px, transparent 1px 4px), radial-gradient(80% 40% at 50% 0%, rgba(182, 255, 46, 0.13), transparent 70%), #05070a',
    ink: '#eaffe0',
    accent: '#b6ff2e',
    card: 'rgba(182, 255, 46, 0.07)',
    line: 'rgba(182, 255, 46, 0.3)',
    button: '#b6ff2e',
    buttonInk: '#05070a',
    radius: '2px',
    buttonRadius: '2px',
    avatarRadius: '3px',
  },
  [SerializedSmartLinkVersionTheme.cassette]: {
    ground: 'radial-gradient(rgba(26, 18, 6, 0.13) 1px, transparent 1px) 0 0 / 9px 9px, #f0dfc2',
    ink: '#1a1206',
    accent: '#dd5320',
    card: '#fff7e8',
    line: '#1a1206',
    button: '#fff7e8',
    buttonInk: '#1a1206',
    radius: '4px',
    buttonRadius: '4px',
    avatarRadius: '4px',
  },
  [SerializedSmartLinkVersionTheme.velvet]: {
    ground:
      'radial-gradient(60% 34% at 50% 6%, rgba(217, 164, 65, 0.16), transparent 70%), radial-gradient(120% 60% at 50% 110%, rgba(0, 0, 0, 0.6), transparent 60%), radial-gradient(120% 80% at 50% -10%, #55142a 0%, #340c1b 52%, #1e0710 100%)',
    ink: '#f7e9da',
    accent: '#d9a441',
    card: 'rgba(255, 255, 255, 0.05)',
    line: 'rgba(217, 164, 65, 0.42)',
    button: '#d9a441',
    buttonInk: '#1e0710',
    radius: '2px',
    buttonRadius: '999px',
    avatarRadius: '50%',
  },
  [SerializedSmartLinkVersionTheme.frost]: {
    ground:
      'radial-gradient(48% 30% at 84% 6%, rgba(120, 170, 255, 0.4), transparent 72%), radial-gradient(52% 32% at 4% 66%, rgba(160, 205, 255, 0.42), transparent 74%), linear-gradient(170deg, #eaf1f8 0%, #dce7f4 46%, #f0f3f7 100%)',
    ink: '#0f1b2a',
    accent: '#2f6bff',
    card: 'rgba(255, 255, 255, 0.62)',
    line: 'rgba(15, 27, 42, 0.12)',
    button: 'rgba(255, 255, 255, 0.74)',
    buttonInk: '#0f1b2a',
    radius: '20px',
    buttonRadius: '999px',
    avatarRadius: '50%',
  },
  [SerializedSmartLinkVersionTheme.bloom]: {
    ground:
      'radial-gradient(60% 30% at 82% 92%, rgba(255, 124, 88, 0.4), transparent 72%), radial-gradient(50% 26% at 10% 8%, rgba(255, 222, 160, 0.5), transparent 72%), linear-gradient(178deg, #fff6ee 0%, #ffe6d9 52%, #ffd6c6 100%)',
    ink: '#2b1710',
    accent: '#ff5c39',
    card: '#ffffff',
    line: 'rgba(43, 23, 16, 0.1)',
    button: '#ffffff',
    buttonInk: '#2b1710',
    radius: '26px',
    buttonRadius: '26px',
    avatarRadius: '50%',
  },
  [SerializedSmartLinkVersionTheme.grid]: {
    ground:
      'linear-gradient(rgba(13, 13, 13, 0.045) 1px, transparent 1px) 0 0 / 100% 8px, linear-gradient(90deg, rgba(13, 13, 13, 0.045) 1px, transparent 1px) 0 0 / 8px 100%, #fafaf9',
    ink: '#0d0d0d',
    accent: '#1f3bff',
    card: '#ffffff',
    line: 'rgba(13, 13, 13, 0.16)',
    button: '#0d0d0d',
    buttonInk: '#fafaf9',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
  [SerializedSmartLinkVersionTheme.ember]: {
    ground:
      'radial-gradient(70% 26% at 50% 100%, rgba(255, 120, 40, 0.3), transparent 70%), repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.018) 0 1px, transparent 1px 3px), radial-gradient(100% 62% at 50% 104%, #47190a 0%, #1a0c06 52%, #0a0604 100%)',
    ink: '#fff1e6',
    accent: '#ff7a1a',
    card: 'rgba(255, 255, 255, 0.05)',
    line: 'rgba(255, 140, 60, 0.26)',
    button: 'linear-gradient(100deg, #ff7a1a, #f0421f)',
    buttonInk: '#1a0c06',
    radius: '8px',
    buttonRadius: '8px',
    avatarRadius: '50%',
  },
  [SerializedSmartLinkVersionTheme.chrome]: {
    ground:
      'linear-gradient(104deg, transparent 26%, rgba(255, 255, 255, 0.06) 44%, transparent 58%), repeating-linear-gradient(96deg, rgba(255, 255, 255, 0.02) 0 2px, transparent 2px 6px), linear-gradient(168deg, #15171c 0%, #0c0d11 40%, #191c22 100%)',
    ink: '#edf1f6',
    accent: '#8fe3ff',
    card: 'rgba(255, 255, 255, 0.06)',
    line: 'rgba(255, 255, 255, 0.18)',
    button: 'linear-gradient(180deg, #ffffff 0%, #c6ccd4 100%)',
    buttonInk: '#0c0d11',
    radius: '14px',
    buttonRadius: '999px',
    avatarRadius: '50%',
  },
  [SerializedSmartLinkVersionTheme.riso]: {
    ground:
      'linear-gradient(0deg, rgba(255, 75, 110, 0.14), rgba(255, 75, 110, 0.14)) 10px 40px / 100% 200px no-repeat, linear-gradient(0deg, rgba(27, 27, 58, 0.1), rgba(27, 27, 58, 0.1)) 0 52px / 100% 200px no-repeat, #f5f1e6',
    ink: '#1b1b3a',
    accent: '#1b1b3a',
    card: '#ffffff',
    line: '#1b1b3a',
    button: '#ff4b6e',
    buttonInk: '#fff8f0',
    radius: '0px',
    buttonRadius: '0px',
    avatarRadius: '0px',
  },
};
